/* Matemática da linha do tempo do storyboard de saques.
   Fica fora do componente para o fast-refresh não quebrar e para os
   invariantes serem testáveis sem montar React. */

/* Storyboard das regras de liberação de saque. Uma linha do tempo com três
   faixas — PIX, cartão e boleto — e um cursor de "hoje" que avança:
   1. cada venda PIX entra na faixa e conta 2 dias até liberar;
   2. vendas de cartão/boleto entram travadas e ficam;
   3. ao passar 1 dia do fim do evento, todas destravam de uma vez;
   4. cada valor liberado voa para o painel "Disponível para saque".
   A regra é temporal — só uma linha do tempo mostra isso. */

/* ── Linha do tempo ── */
export const DAYS = 10;
export const T0 = 60;
export const T_STEP = 27.5;
export const dayX = (d: number) => T0 + d * T_STEP;
export const xToDay = (x: number) => (x - T0) / T_STEP;

export const PIX_Y = 70;
export const CARD_Y = 130;
export const BOLETO_Y = 190;
export const AXIS_Y = 232;

export const EVENT_END = 7;
export const CARD_RELEASE = EVENT_END + 1;
const PIX_LAG = 2;
const FLY = 0.8;

export const CARD_COLOR = "#8B5CF6";
export const BOLETO_COLOR = "#64748B";
export const GREEN = "hsl(142 71% 45%)";
export const GREEN_TEXT = "hsl(142 71% 32%)";

export interface Sale {
  lane: "pix" | "card" | "boleto";
  day: number;
  value: number;
}

export const SALES: Sale[] = [
  { lane: "pix", day: 0.4, value: 120 },
  { lane: "card", day: 1.1, value: 200 },
  { lane: "boleto", day: 1.8, value: 140 },
  { lane: "pix", day: 2.3, value: 80 },
  { lane: "card", day: 3.4, value: 90 },
  { lane: "pix", day: 4.6, value: 60 },
];

export interface WithdrawalEvent {
  id: string;
  day: number;
  amount: number;
  label: string;
}

export const WITHDRAWAL_EVENTS: WithdrawalEvent[] = [
  { id: "wd-1", day: 3.2, amount: 120, label: "Saque PIX" },
  { id: "wd-2", day: 9.0, amount: 430, label: "Saque pós-evento" },
];

const releaseDay = (s: Sale) => (s.lane === "pix" ? s.day + PIX_LAG : CARD_RELEASE);

/* ── Painel da direita ── */
export const PANEL_X = 372;
export const BOX_X = 386;
export const BOX_W = 106;
export const DOCK = { x: 439, y: 76 };

/* ── Relógio ── */
const END_DAY = DAYS + 0.5;
const DAY_MS = 1000;
export const RUN = END_DAY * DAY_MS;
export const HOLD = 1800;
const RESET = 700;
export const CYCLE = RUN + HOLD + RESET;
export const DEMO_TIMING = { RUN, HOLD, RESET, CYCLE, END_DAY, FLY, PIX_LAG, CARD_RELEASE };
const FADE_IN = 420;

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const smoothstep = (t: number) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};
const bump = (t: number) => (t < 0 || t > 1 ? 0 : Math.sin(t * Math.PI));

export const brl = (v: number) => `R$ ${Math.round(v).toLocaleString("pt-BR")}`;

type Phase = "hidden" | "waiting" | "flying" | "docked";

export interface CoinState {
  x: number;
  y: number;
  scale: number;
  opacity: number;
  phase: Phase;
  progress: number; // 0..1 — só PIX, quanto já correu do D+2
  locked: number; // 0..1 — opacidade do cadeado
}

export interface Frame {
  clock: number;
  coins: CoinState[];
  available: number;
  toRelease: number;
  withdrawn: number;
  landPulse: number;
  unlockPulse: number;
  stage: number; // fade do ciclo inteiro
  finished: boolean;
}

export function computeFrame(e: number): Frame {
  const clock = e < RUN ? (e / RUN) * END_DAY : END_DAY;
  const finished = e >= RUN;

  // fade de entrada e saída do ciclo — evita o corte seco ao reiniciar
  const stage = Math.min(smoothstep(e / FADE_IN), e > CYCLE - RESET ? smoothstep((CYCLE - e) / RESET) : 1);

  let available = 0;
  let toRelease = 0;
  let landPulse = 0;

  const coins: CoinState[] = SALES.map((s) => {
    const r = releaseDay(s);
    const laneY = s.lane === "pix" ? PIX_Y : s.lane === "card" ? CARD_Y : BOLETO_Y;
    const home = { x: dayX(s.day), y: laneY };

    // pesos contínuos: os números sobem junto com o voo, sem saltar
    const appearW = smoothstep((clock - s.day) / 0.3);
    const landW = smoothstep((clock - (r + FLY - 0.25)) / 0.5);
    available += s.value * landW;
    toRelease += s.value * appearW * (1 - landW);
    landPulse = Math.max(landPulse, bump((clock - (r + FLY - 0.1)) / 0.6));

    if (clock < s.day) {
      return { ...home, scale: 0.92, opacity: 0, phase: "hidden", progress: 0, locked: 0 };
    }

    if (clock < r) {
      const t = clamp01((clock - s.day) / 0.3);
      const drop = (1 - easeOut(t)) * 7;
      const progress = s.lane === "pix" ? clamp01((clock - s.day) / PIX_LAG) : clamp01((clock - s.day) / (r - s.day));
      // cartão dá um pequeno impulso pouco antes de destravar
      const nudge = s.lane !== "pix" ? bump((clock - (r - 0.3)) / 0.3) * 0.05 : 0;
      return {
        x: home.x,
        y: home.y - drop,
        scale: lerp(0.92, 1, easeOut(t)) + nudge,
        opacity: easeOut(t),
        phase: "waiting",
        progress,
        locked: s.lane !== "pix" ? 1 - clamp01((clock - (r - 0.25)) / 0.25) : 0,
      };
    }

    const flown = (clock - r) / FLY;
    if (flown < 1) {
      const k = easeInOut(flown);
      return {
        x: lerp(home.x, DOCK.x, k),
        y: lerp(home.y, DOCK.y, k) - Math.sin(k * Math.PI) * 24,
        scale: lerp(1, 0.5, k),
        opacity: 1 - smoothstep((flown - 0.68) / 0.32),
        phase: "flying",
        progress: 1,
        locked: 0,
      };
    }

    return { x: DOCK.x, y: DOCK.y, scale: 0.5, opacity: 0, phase: "docked", progress: 1, locked: 0 };
  });

  const unlockPulse = bump((clock - CARD_RELEASE) / 0.8);
  const withdrawn = WITHDRAWAL_EVENTS.reduce(
    (sum, w) => (clock >= w.day ? sum + w.amount : sum),
    0
  );

  return { clock, coins, available, toRelease, withdrawn, landPulse, unlockPulse, stage, finished };
}
